import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Loader, Popover } from '@mantine/core';
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import iconFlora from '../../../../assets/icons/iconFlora.svg';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import iconADO from '../../../integrations/assets/iconADO.svg';
import iconJira from '../../../integrations/assets/iconJiraCloud.svg';
import { getInternalPath, getPreviewTarget, initialsFor } from './agent-link-preview.helpers';
import { useLinkPreviewDetails } from './agent-link-preview.hooks';
import { PreviewSource } from './agent-link-preview.type';
import type { AgentLinkPreviewProps, PreviewTarget } from './agent-link-preview.type';

/**
 * Renders a Markdown link from a Flora message. Links to Jira issues, Azure
 * DevOps work items and Bloomfilter pages get a hover/focus preview card; other
 * in-app links route client-side, and external links open in a new tab.
 *
 * @param {AgentLinkPreviewProps} props - Anchor props from the Markdown renderer; `href` decides the behaviour
 * @returns {JSX.Element} The link, wrapped in a preview popover when the target is recognised
 */
export function AgentLinkPreview({ href, children, ...props }: AgentLinkPreviewProps) {
  const jiraDomain = useGlobalStore((state) => state.organization?.jira_domain);
  const internalPath = getInternalPath(href);
  const target = getPreviewTarget(href, jiraDomain);
  if (target) {
    return (
      <PreviewedLink href={href} internalPath={internalPath} target={target} linkProps={props}>
        {children}
      </PreviewedLink>
    );
  }
  return internalPath ? (
    <Link {...props} to={internalPath}>
      {children}
    </Link>
  ) : (
    <a {...props} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

/**
 * Wraps a recognised link in a preview popover. Opens after a short hover
 * delay or immediately on keyboard focus, closes on leave, blur, click or
 * Escape, and only loads details while open. Focus that comes from a pointer
 * press, or from the window regaining focus after the link was followed, does
 * not reopen the card. Links on hosts not known to belong to the source show
 * the card only once a lookup confirms the item exists.
 *
 * @param {object} props - Component props
 * @param {string | undefined} props.href - The original href, used for external links
 * @param {string | undefined} props.internalPath - Router path when the link stays inside the app
 * @param {PreviewTarget} props.target - The parsed preview target
 * @param {Omit<AgentLinkPreviewProps, 'href' | 'children'>} props.linkProps - Remaining anchor props to forward
 * @param {ReactNode} props.children - The link text
 * @returns {JSX.Element} The link and its preview card
 */
function PreviewedLink({
  href,
  internalPath,
  target,
  linkProps,
  children,
}: {
  href: string | undefined;
  internalPath: string | undefined;
  target: PreviewTarget;
  linkProps: Omit<AgentLinkPreviewProps, 'href' | 'children'>;
  children: ReactNode;
}) {
  const [opened, setOpened] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Set when the link is pressed or followed, so the focus that follows (or
  // the refocus when the user returns from the tab it opened) doesn't pin the
  // card open. Cleared once focus genuinely moves elsewhere on the page, or
  // when the user starts keyboard navigation.
  const skipFocusOpen = useRef(false);
  useEffect(() => {
    // Tab means the next focus is keyboard focus, which should always open the
    // card, even if no blur or mouse-leave cleared the flag since the click.
    const clearOnTab = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Tab') skipFocusOpen.current = false;
    };
    document.addEventListener('keydown', clearOnTab, true);
    return () => {
      document.removeEventListener('keydown', clearOnTab, true);
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    };
  }, []);
  const linkHandlers = {
    onMouseEnter: () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
      openTimer.current = setTimeout(() => setOpened(true), 350);
    },
    onMouseLeave: (event: MouseEvent<HTMLAnchorElement>) => {
      clearTimeout(openTimer.current);
      const focused = document.activeElement === event.currentTarget;
      // Keyboard focus keeps the card open; focus from a click does not.
      if (focused && !skipFocusOpen.current) return;
      // Browsers that don't focus links on click (Safari, Firefox on macOS)
      // never send the blur that would clear the flag, so clear it here.
      if (!focused) skipFocusOpen.current = false;
      closeTimer.current = setTimeout(() => setOpened(false), 150);
    },
    onMouseDown: (event: MouseEvent<HTMLAnchorElement>) => {
      if (event.button === 0) skipFocusOpen.current = true;
    },
    onClick: () => {
      skipFocusOpen.current = true;
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
      setOpened(false);
    },
    onFocus: () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
      if (skipFocusOpen.current) return;
      setOpened(true);
    },
    onBlur: () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
      setOpened(false);
      // A blur while the window is losing focus (e.g. the link opened a new
      // tab) is followed by a refocus on return; keep skipping that one.
      if (document.hasFocus()) skipFocusOpen.current = false;
    },
    onKeyDown: (event: KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key === 'Escape') setOpened(false);
    },
  };

  const link = internalPath ? (
    <Link {...linkProps} {...linkHandlers} to={internalPath}>
      {children}
    </Link>
  ) : (
    <a {...linkProps} {...linkHandlers} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );

  const { details, isFetching } = useLinkPreviewDetails(target, opened);
  // Links on unrecognised hosts only look like Jira/ADO URLs, so the lookup
  // still runs on hover but the card stays hidden until it finds the item.
  const showPreview = opened && (target.knownHost || !!details.matched);
  const sourceIcon =
    target.source === PreviewSource.JIRA
      ? iconJira
      : target.source === PreviewSource.AZURE_DEVOPS
        ? iconADO
        : iconFlora;
  const sourceDetail =
    target.id && target.source !== PreviewSource.BLOOMFILTER
      ? target.source === PreviewSource.AZURE_DEVOPS
        ? `#${target.id}`
        : target.id
      : target.kind;
  const previewTitle =
    details.title ||
    (target.source === PreviewSource.BLOOMFILTER
      ? target.title
      : isFetching
        ? `Loading ${target.kind.toLowerCase()}…`
        : `${target.kind} details unavailable`);
  // Tasks always have an assignee slot once their details load; Bloomfilter
  // pages and initiatives have no assignee, so they skip the footer.
  const showAssignee = !!details.assignee || (target.source !== PreviewSource.BLOOMFILTER && !!details.assigneeKnown);
  return (
    <Popover opened={showPreview} onChange={setOpened} position="top" offset={8} width={360} withinPortal={false}>
      <Popover.Target>{link}</Popover.Target>
      <PreviewDropdown data-source={target.source}>
        <PreviewHeader>
          <PreviewIcon src={sourceIcon} alt="" />
          <PreviewSourceLabel>{target.source}</PreviewSourceLabel>
          <PreviewKey>{sourceDetail}</PreviewKey>
          {(details.type || details.status) && (
            <PreviewPills>
              {details.type && <PreviewType title={details.type}>{details.type}</PreviewType>}
              {details.status && (
                <PreviewStatus title={details.status}>
                  <PreviewStatusDot aria-hidden="true" />
                  <PreviewVisuallyHidden>Status: </PreviewVisuallyHidden>
                  <PreviewStatusText>{details.status}</PreviewStatusText>
                </PreviewStatus>
              )}
            </PreviewPills>
          )}
        </PreviewHeader>
        <PreviewBody>
          {details.epicName && (
            <PreviewEpic>
              <Icon icon="material-symbols:account-tree-outline" width={14} height={14} aria-hidden="true" />
              <PreviewVisuallyHidden>Epic: </PreviewVisuallyHidden>
              {details.epicKey && <PreviewEpicKey>{details.epicKey}</PreviewEpicKey>}{' '}
              <PreviewEpicName title={details.epicName}>{details.epicName}</PreviewEpicName>
            </PreviewEpic>
          )}
          <PreviewTitle>{previewTitle}</PreviewTitle>
          {details.description && <PreviewDescription>{details.description}</PreviewDescription>}
        </PreviewBody>
        {(showAssignee || isFetching) && (
          <PreviewFooter>
            {details.assignee ? (
              <PreviewAssignee>
                <PreviewAvatar aria-hidden="true">{initialsFor(details.assignee)}</PreviewAvatar>
                <PreviewAssigneeName>
                  <PreviewVisuallyHidden>Assigned to </PreviewVisuallyHidden>
                  {details.assignee}
                </PreviewAssigneeName>
              </PreviewAssignee>
            ) : (
              showAssignee && <PreviewUnassigned>Unassigned</PreviewUnassigned>
            )}
            {isFetching && <Loader size={14} aria-label="Loading link preview" />}
          </PreviewFooter>
        )}
      </PreviewDropdown>
    </Popover>
  );
}

const PreviewDropdown = styled(Popover.Dropdown)`
  width: 360px;
  max-width: min(360px, calc(100vw - 32px));
  box-sizing: border-box;
  padding: 0;
  border: 1px solid var(--sky-base);
  border-radius: 12px;
  background: var(--base-white);
  color: var(--ink-darkest);
  box-shadow: var(--box-shadow-medium-hard);
  word-break: normal;
  overflow-wrap: anywhere;
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--sky-light);
  background: var(--sky-lightest);
`;

const PreviewIcon = styled.img`
  display: block;
  flex: none;
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const PreviewSourceLabel = styled.div`
  flex: none;
  white-space: nowrap;
  color: var(--ink-light);
  font-size: 12px;
  font-weight: 600;
`;

const PreviewKey = styled.div`
  flex: none;
  white-space: nowrap;
  color: var(--ink-darkest);
  font-size: 12px;
  font-weight: 700;
`;

const PreviewPills = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 60%;
  margin-left: auto;
`;

const PreviewType = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 2px 8px;
  border: 1px solid var(--sky-light);
  border-radius: 32px;
  background: var(--base-white);
  color: var(--ink-dark);
  font-size: 11px;
  font-weight: 600;
`;

const PreviewStatus = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 0 1 auto;
  min-width: 0;
  padding: 2px 8px;
  border-radius: 32px;
  background: var(--secondary-lightest);
  color: var(--flora-link-preview-accent-text, var(--secondary-darkest));
  font-size: 11px;
  font-weight: 600;
`;

const PreviewStatusText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewStatusDot = styled.span`
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--secondary-base);
`;

const PreviewEpic = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: var(--ink-light);
  font-size: 12px;

  svg {
    flex: none;
  }
`;

const PreviewEpicKey = styled.span`
  flex: none;
  color: var(--ink-dark);
  font-weight: 600;
  white-space: nowrap;
`;

const PreviewEpicName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px 14px;
`;

const PreviewTitle = styled.div`
  color: var(--ink-darkest);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  text-wrap: pretty;
`;

const PreviewDescription = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  color: var(--ink-base);
  font-size: 13px;
  line-height: 1.5;
`;

const PreviewFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--sky-light);
`;

const PreviewAssignee = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: var(--ink-base);
  font-size: 12px;
`;

const PreviewAvatar = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--secondary-lightest);
  color: var(--flora-link-preview-accent-text, var(--secondary-darkest));
  font-size: 9px;
  font-weight: 700;
`;

const PreviewAssigneeName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewVisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const PreviewUnassigned = styled.div`
  color: var(--ink-light);
  font-size: 12px;
`;
